"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.numberType = void 0;
const actual_type_1 = require("./actual-type");
const caster_cache_1 = require("./caster-cache");
const options_1 = require("../options");
function numberType(declaration, validateOptions, circularCache = caster_cache_1.createCache()) {
    if (declaration === null || typeof declaration !== 'object') {
        throw new Error('First argument (declaration) must be an Object.');
    }
    const circularCaster = circularCache.get(declaration);
    if (circularCaster !== undefined) {
        return circularCaster;
    }
    const { type, required, strict, round, precision, exclusiveMaximum, exclusiveMinimum, maximum, minimum, multipleOf } = declaration;
    if (type !== 'Number') {
        throw new Error('"type" property on declaration must be "Number"');
    }
    if (required !== undefined && typeof required !== 'boolean') {
        throw new Error('"required" property on declaration must be undefined or a Boolean');
    }
    if (strict !== undefined && typeof strict !== 'boolean') {
        throw new Error('"strict" property on declaration must be undefined or a Boolean');
    }
    if (round !== undefined && typeof round !== 'boolean') {
        throw new Error('"round" property on declaration must be undefined or a Boolean');
    }
    const defaultCandidate = Reflect.get(declaration, 'default');
    if (defaultCandidate !== undefined && typeof defaultCandidate !== 'number') {
        throw new Error('"default" property on declaration must be undefined or a Number');
    }
    let caster = (candidate) => ({ type, isValid: true, invalidations: [], candidate });
    const options = Array.isArray(declaration.options) ? Array.from(declaration.options) : declaration.options;
    if (options !== undefined) {
        if (Array.isArray(options) === false || options.find((opt) => typeof opt !== 'number') !== undefined) {
            throw new Error('"options" property on declaration must undefined or an array of Number');
        }
        const casterRef = caster;
        caster = (candidate) => {
            const result = casterRef(candidate);
            if (options.find((opt) => opt === candidate) === undefined) {
                return {
                    ...result,
                    invalidations: [[[], 'Number', 'options', options, candidate], ...result.invalidations]
                };
            }
            return result;
        };
    }
    if (precision !== undefined) {
        if (typeof precision !== 'number' || precision % 1 !== 0 || precision < 0) {
            throw new Error('"precision" property on declaration must undefined or a Number of non-negative integer');
        }
        const casterRef = caster;
        caster = (candidate) => {
            const result = casterRef(candidate);
            if (candidate !== roundNumber(candidate, precision)) {
                return {
                    ...result,
                    invalidations: [[[], 'Number', 'precision', precision, candidate], ...result.invalidations]
                };
            }
            return result;
        };
    }
    if (exclusiveMaximum !== undefined) {
        if (typeof exclusiveMaximum !== 'number') {
            throw new Error('"exclusiveMaximum" property on declaration must undefined or a Number');
        }
        const casterRef = caster;
        caster = (candidate) => {
            const result = casterRef(candidate);
            if (exclusiveMaximum <= candidate) {
                return {
                    ...result,
                    invalidations: [[[], 'Number', 'exclusiveMaximum', exclusiveMaximum, candidate], ...result.invalidations]
                };
            }
            return result;
        };
    }
    if (exclusiveMinimum !== undefined) {
        if (typeof exclusiveMinimum !== 'number') {
            throw new Error('"exclusiveMinimum" property on declaration must undefined or a Number');
        }
        const casterRef = caster;
        caster = (candidate) => {
            const result = casterRef(candidate);
            if (exclusiveMinimum >= candidate) {
                return {
                    ...result,
                    invalidations: [[[], 'Number', 'exclusiveMinimum', exclusiveMinimum, candidate], ...result.invalidations]
                };
            }
            return result;
        };
    }
    if (maximum !== undefined) {
        if (typeof maximum !== 'number') {
            throw new Error('"maximum" property on declaration must undefined or a Number');
        }
        const casterRef = caster;
        caster = (candidate) => {
            const result = casterRef(candidate);
            if (maximum < candidate) {
                return {
                    ...result,
                    invalidations: [[[], 'Number', 'maximum', maximum, candidate], ...result.invalidations]
                };
            }
            return result;
        };
    }
    if (minimum !== undefined) {
        if (typeof minimum !== 'number') {
            throw new Error('"minimum" property on declaration must undefined or a Number');
        }
        const casterRef = caster;
        caster = (candidate) => {
            const result = casterRef(candidate);
            if (minimum > candidate) {
                return {
                    ...result,
                    invalidations: [[[], 'Number', 'minimum', minimum, candidate], ...result.invalidations]
                };
            }
            return result;
        };
    }
    if (multipleOf !== undefined) {
        if (typeof multipleOf !== 'number') {
            throw new Error('"multipleOf" property on declaration must undefined or a Number');
        }
        const casterRef = caster;
        caster = (candidate) => {
            const result = casterRef(candidate);
            if (candidate % multipleOf !== 0) {
                return {
                    ...result,
                    invalidations: [[[], 'Number', 'multipleOf', multipleOf, candidate], ...result.invalidations]
                };
            }
            return result;
        };
    }
    const separator = options_1.getOption(validateOptions, ['Number', 'format', 'separator']);
    const delimiter = options_1.getOption(validateOptions, ['Number', 'format', 'delimiter']);
    const delimiterRegExp = new RegExp(`\\${delimiter}`, 'g');
    const casterRef = (candidate) => {
        const originalCandidate = candidate;
        if (strict === false && typeof candidate === 'string' && candidate.trim().length === 0) {
            candidate = undefined;
        }
        if ((candidate === undefined || candidate === null) && defaultCandidate !== undefined) {
            candidate = defaultCandidate;
        }
        if (candidate === undefined || candidate === null) {
            if (required === false) {
                return {
                    type,
                    isValid: true,
                    invalidations: [],
                    candidate
                };
            }
            else {
                return {
                    type,
                    isValid: false,
                    candidate,
                    invalidations: [[[], 'Number', 'required', true, candidate]]
                };
            }
        }
        if (strict === false) {
            if (typeof candidate === 'string') {
                if (/^\d+$/.test(candidate)) {
                    candidate = Number.parseFloat(candidate);
                }
                else {
                    const fragments = candidate.split(separator);
                    if (fragments.length === 1) {
                        const integer = fragments[0].replace(delimiterRegExp, '');
                        if (/^\d+$/.test(integer)) {
                            candidate = Number.parseFloat(integer);
                        }
                    }
                    else if (fragments.length === 2) {
                        const integer = fragments[0].replace(delimiterRegExp, '');
                        const fractional = fragments[1];
                        if (/^\d+$/.test(integer) && /^\d+$/.test(fractional)) {
                            candidate = Number.parseFloat(`${integer}.${fractional}`);
                        }
                    }
                }
            }
        }
        const candidateType = actual_type_1.ctorName(candidate);
        if (candidateType !== type) {
            return {
                type,
                isValid: false,
                candidate,
                invalidations: [[[], 'Number', 'type', type, candidateType]]
            };
        }
        if (round === true) {
            candidate = Math.round(candidate);
        }
        const result = caster(candidate);
        const isValid = result.invalidations.length === 0;
        return {
            ...result,
            isValid,
            candidate: isValid ? candidate : originalCandidate
        };
    };
    return circularCache.set(declaration, casterRef);
}
exports.numberType = numberType;
function roundNumber(num, scale) {
    if (scale === 0) {
        return Math.round(num);
    }
    if (('' + num).includes('e') === false) {
        return +(Math.round(num + 'e+' + scale) + 'e-' + scale);
    }
    else {
        const arr = ('' + num).split('e');
        let sig = '';
        if (+arr[1] + scale > 0) {
            sig = '+';
        }
        return +(Math.round(+arr[0] + 'e' + sig + (+arr[1] + scale)) + 'e-' + scale);
    }
}
